/**
 * Hard-stop banner for chats whose agent needs the File System Access
 * API but is running on a browser that doesn't support it (Firefox /
 * Safari). The "no folder attached" and "needs reconnect" cases used to
 * live here too — those have moved into the prompt-toolbar
 * {@link FolderSelector} chip so chat surface stays uncluttered.
 *
 * Renders nothing when the browser is supported.
 *
 * @module file-agent/components/browser-workspace-banner
 */

import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { FolderAttachment } from "../lib/use-folder-attachment";

interface BrowserWorkspaceBannerProps {
  attachment: FolderAttachment;
  className?: string;
}

/**
 * Show only when the browser cannot run the File System Access API. The
 * chip handles the recoverable states inline.
 */
export function BrowserWorkspaceBanner({
  attachment,
  className,
}: BrowserWorkspaceBannerProps) {
  const { ready, supported } = attachment;

  // Avoid flashing the banner while we check storage on mount.
  if (!ready) return null;
  if (supported) return null;

  return (
    <div
      className={cn(
        "mx-auto w-full max-w-3xl xl:max-w-4xl px-4 pt-3",
        className,
      )}
    >
      <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertTriangle className="size-4 shrink-0 text-destructive mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-foreground">
            This agent needs Chrome or Edge
          </p>
          <p className="mt-1 text-muted-foreground">
            File access uses a browser feature your current browser doesn't support.
            Open this chat in Chrome or Edge to give the agent access to your local files.
          </p>
        </div>
      </div>
    </div>
  );
}
